import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { isEqual } from 'lodash';
import { BehaviorSubject, map } from 'rxjs';

import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';
import { SiteModel } from '@models/sites/sites.store';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { Country, Currency } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Maybe, Utils } from '@shared/utils/utils';

interface SiteForm {
  address_line_1: Maybe<string>;
  address_line_2: Maybe<string>;
  address_line_3: Maybe<string>;
  city: Maybe<string>;
  closeout_date: Maybe<string>;
  country: Maybe<string>;
  currency: Maybe<string>;
  family_name: Maybe<string>;
  given_name: Maybe<string>;
  investigator_id: Maybe<string>;
  managed_by_id: Maybe<string>;
  name: Maybe<string>;
  site_activation: Maybe<string>;
  site_id: null | string;
  site_no: Maybe<string>;
  state: Maybe<string>;
  target_patients: Maybe<number>;
  zip: Maybe<string>;
}

@Component({
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    NgSelectComponent,
    FormErrorDirective,
    ButtonComponent,
    AsyncPipe,
  ],
  selector: 'aux-site-information-edit-view',
  templateUrl: './site-information-edit-view.component.html',
})
export class SiteInformationEditViewComponent implements OnInit {
  private fb = inject(FormBuilder);
  private overlayService = inject(OverlayService);
  private sitesQuery = inject(SitesQuery);
  private sitesService = inject(SitesService);
  private vendorQuery = inject(OrganizationQuery);

  readonly closeoutInput = viewChild<InputComponent>('closeoutInput');

  readonly countryOptions = Utils.getCountriesForSelectOptions();
  readonly currencyOptions = Utils.CURRENCY_OPTIONS;
  @Input({ required: true }) editMode$!: BehaviorSubject<boolean>;
  @Output() formChanged = new EventEmitter<boolean>();
  initialFormState!: SiteForm;
  @Input({ required: true }) site!: SiteModel & {
    investigator_name?: string;
  };
  siteForm = this.fb.group<SiteForm>({
    address_line_1: null,
    address_line_2: null,
    address_line_3: null,
    city: null,
    closeout_date: null,
    country: null,
    currency: null,
    family_name: null,
    given_name: null,
    investigator_id: null,
    managed_by_id: null,
    name: null,
    site_activation: null,
    site_id: null,
    site_no: null,
    state: null,
    target_patients: null,
    zip: null,
  });
  vendorOptions$ = this.vendorQuery
    .selectAll()
    .pipe(map((vendors) => vendors.map((vendor) => ({ label: vendor.name, value: vendor.id }))));

  ngOnInit(): void {
    this.setInitialValues();
  }

  cancelChanges() {
    this.editMode$.next(false);
  }

  constructor() {
    this.siteForm.valueChanges.pipe(takeUntilDestroyed()).subscribe((values) => {
      if (this.initialFormState) {
        this.formChanged.emit(
          !isEqual(this.nullifyEmptyStringValues(<SiteForm>values), this.initialFormState)
        );
      }
    });
  }

  nullifyEmptyStringValues(formValues: SiteForm) {
    return Object.entries(formValues).reduce((accum, [key, value]) => {
      return {
        ...accum,
        [key]: value === '' ? null : value,
      };
    }, {}) as SiteForm;
  }

  onBlurSiteActivationPicker(closeoutDate: InputComponent) {
    // mark as touched and trigger validation
    closeoutDate.fc.markAsTouched();
    closeoutDate.fc.markAsDirty();
    closeoutDate.onBlur();
  }

  // eslint-disable-next-line complexity
  private setInitialValues() {
    this.siteForm.patchValue({
      address_line_1: this.site.address_line_1 || null,
      address_line_2: this.site.address_line_2 || null,
      address_line_3: this.site.address_line_3 || null,
      city: this.site.city || null,
      closeout_date: this.site.closeout_date || null,
      country: this.site.country || null,
      currency: this.site.currency || null,
      family_name: this.site.investigator?.family_name || null,
      given_name: this.site.investigator?.given_name || null,
      investigator_id: this.site.investigator?.id || null,
      managed_by_id: this.site.managed_by_id || null,
      name: this.site.name || null,
      site_activation: this.site.site_activation || null,
      site_id: this.site.id,
      site_no: this.site.site_no || null,
      state: this.site.state || null,
      target_patients: this.site.target_patients || null,
      zip: this.site.zip || null,
    });

    this.initialFormState = this.siteForm.getRawValue();

    this.formChanged.emit(false);
  }

  get siteNoValidators(): ValidatorFn[] {
    const siteNumbersToForbid = this.sitesQuery
      .getAll()
      .map((site) => site.site_no)
      .filter((site) => site !== this.site.site_no);

    return [
      Validators.required,
      CustomValidators.valueNotInList(siteNumbersToForbid, 'duplicatedSite'),
    ];
  }

  async updateSite() {
    this.siteForm.markAllAsTouched();

    if (this.siteForm.invalid || this.closeoutInput()?.fc.invalid) {
      return;
    }

    const {
      address_line_1,
      address_line_2,
      address_line_3,
      city,
      closeout_date,
      country,
      currency,
      family_name,
      given_name,
      investigator_id,
      managed_by_id,
      name,
      site_activation,
      site_id,
      site_no,
      state,
      target_patients,
      zip,
    } = this.siteForm.getRawValue();

    const siteUpdateResponse = await this.sitesService.update(
      site_id as string,
      {
        address_line_1,
        address_line_2,
        address_line_3,
        city: city || '',
        closeout_date,
        country: country as Country,
        currency: currency as Currency,
        id: site_id as string,
        managed_by_id,
        name: name || '',
        site_activation,
        site_no: site_no || '',
        state,
        target_patients,
        zip,
      },
      {
        family_name: family_name || '',
        given_name: given_name || '',
        id: investigator_id || '',
      },
      false,
      this.site.managed_by_id || undefined
    );

    if (siteUpdateResponse.success) {
      this.editMode$.next(false);
      this.formChanged.emit(false);
      this.overlayService.success(`${name} successfully updated!`);
    }
  }
}
